import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import ClientSimpleBarChart from "../../clientPortal/ClientSimpleBarChart";
import {
  fetchAdminVehicleMarketAnalyticsOverview,
  fetchVehicleMarketAnalyticsComparison,
  fetchVehicleMarketAnalyticsFilters,
  fetchVehicleMarketAnalyticsOverview,
} from "../../../services/mobilityVehicleMarketAnalyticsApi";
import {
  transportPathForSection,
  TRANSPORT_SECTIONS,
  vehicleMarketAnalyticsClassPath,
} from "../../../constants/transport";
import { formatCop } from "./vehicleMarketFormat";
import VehicleClassAnalyticsCard from "./VehicleClassAnalyticsCard";
import "../../../styles/vehicleMarket.css";

const FILTER_KEYS = [
  "department",
  "city",
  "vehicle_class",
  "make",
  "year_min",
  "year_max",
  "price_min",
  "price_max",
];

function readFilters(params) {
  const filters = {};
  for (const key of FILTER_KEYS) {
    const v = params.get(key);
    if (v) filters[key] = v;
  }
  return filters;
}

const VehicleMarketAnalyticsPage = ({ adminMode = false, onClose }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => readFilters(searchParams), [searchParams]);
  const [overview, setOverview] = useState(null);
  const [comparison, setComparison] = useState([]);
  const [facetOptions, setFacetOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const queryParams = {
    ...filters,
    ...(adminMode
      ? {
          raw_listing_counts: "true",
          show_poor_quality: searchParams.get("show_poor_quality") || undefined,
          include_removed: searchParams.get("include_removed") || undefined,
        }
      : {}),
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [overviewData, comparisonData, facets] = await Promise.all([
        adminMode
          ? fetchAdminVehicleMarketAnalyticsOverview(queryParams)
          : fetchVehicleMarketAnalyticsOverview(queryParams),
        fetchVehicleMarketAnalyticsComparison(queryParams),
        fetchVehicleMarketAnalyticsFilters(queryParams),
      ]);
      setOverview(overviewData);
      setComparison(comparisonData.items || []);
      setFacetOptions(facets.filters || facets);
    } catch (err) {
      setOverview(null);
      setError(err.message || "Unable to load market analytics.");
    } finally {
      setLoading(false);
    }
  }, [filters, adminMode, searchParams]);

  useEffect(() => {
    load();
  }, [load]);

  const updateFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  const totals = overview?.totals;
  const classes = overview?.classes || [];

  const vehicleCountChart = classes.map((c) => ({
    label: c.vehicleClassName || c.vehicleClass,
    value: c.activeVehicleCount,
  }));

  const medianPriceChart = classes.map((c) => ({
    label: c.vehicleClassName || c.vehicleClass,
    value: c.medianPriceCop || 0,
  }));

  const fairValueChart = classes.flatMap((c) => [
    { label: `${c.vehicleClass} great`, value: c.greatDealCount || 0 },
    { label: `${c.vehicleClass} good`, value: c.goodDealCount || 0 },
  ]);

  return (
    <div className="vehicle-analytics-page">
      <header className="vehicle-market-page__header">
        <div>
          <h1>Vehicle Market Analytics</h1>
          <p className="muted">
            Class-level supply, pricing, fair-value, and price-drop signals from active listings.
          </p>
        </div>
        <div className="vehicle-market-page__header-actions">
          {!adminMode ? (
            <Link
              to={transportPathForSection(TRANSPORT_SECTIONS.VEHICLES)}
              className="crm-btn crm-btn-secondary"
            >
              Back to Vehicle Market
            </Link>
          ) : null}
          {onClose ? (
            <button type="button" className="crm-btn crm-btn-secondary" onClick={onClose}>
              Close
            </button>
          ) : null}
        </div>
      </header>

      <div className="vehicle-analytics-page__filters panel-card">
        <label>
          Department
          <select
            value={filters.department || ""}
            onChange={(e) => updateFilter("department", e.target.value)}
          >
            <option value="">All</option>
            {(facetOptions?.departments || []).map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </label>
        <label>
          City
          <select value={filters.city || ""} onChange={(e) => updateFilter("city", e.target.value)}>
            <option value="">All</option>
            {(facetOptions?.cities || []).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label>
          Class
          <select
            value={filters.vehicle_class || ""}
            onChange={(e) => updateFilter("vehicle_class", e.target.value)}
          >
            <option value="">All classes</option>
            {(facetOptions?.vehicleClasses || []).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
      </div>

      {loading ? <p className="vehicle-analytics-page__status">Loading analytics…</p> : null}
      {error ? <div className="panel-card vehicle-analytics-page__error">{error}</div> : null}

      {!loading && !error && totals ? (
        <>
          <div className="vehicle-analytics-page__totals">
            <div className="panel-card vehicle-analytics-total">
              <span>Active vehicles</span>
              <strong>{totals.totalActiveVehicles}</strong>
            </div>
            <div className="panel-card vehicle-analytics-total">
              <span>Active listings</span>
              <strong>{totals.totalActiveListings}</strong>
            </div>
            <div className="panel-card vehicle-analytics-total">
              <span>Median market price</span>
              <strong>{formatCop(totals.medianMarketPriceCop)}</strong>
            </div>
            <div className="panel-card vehicle-analytics-total">
              <span>Price drops (30d)</span>
              <strong>{totals.totalPriceDrops30d}</strong>
            </div>
            {overview.adminExtras ? (
              <div className="panel-card vehicle-analytics-total">
                <span>Open quality issues</span>
                <strong>{overview.adminExtras.open_quality_issues ?? 0}</strong>
              </div>
            ) : null}
          </div>

          <section className="vehicle-analytics-page__charts">
            <div className="panel-card">
              <ClientSimpleBarChart
                title="Active vehicles by class"
                data={vehicleCountChart}
                labelKey="label"
                valueKey="value"
              />
            </div>
            <div className="panel-card">
              <ClientSimpleBarChart
                title="Median price by class (COP)"
                data={medianPriceChart}
                labelKey="label"
                valueKey="value"
              />
            </div>
          </section>

          <section className="panel-card">
            <h2>Class comparison</h2>
            <table className="vehicle-detail-table">
              <thead>
                <tr>
                  <th>Class</th>
                  <th>Vehicles</th>
                  <th>Median price</th>
                  <th>Median mileage</th>
                  <th>Drops 30d</th>
                  <th>Good/great</th>
                  <th>Overpriced</th>
                </tr>
              </thead>
              <tbody>
                {comparison.map((row) => (
                  <tr key={row.vehicleClass}>
                    <td>
                      <Link to={vehicleMarketAnalyticsClassPath(row.vehicleClass)}>
                        {row.vehicleClassName}
                      </Link>
                    </td>
                    <td>{row.activeVehicleCount}</td>
                    <td>{formatCop(row.medianPriceCop)}</td>
                    <td>{row.medianMileageKm ?? "—"}</td>
                    <td>{row.priceDropCount30d ?? 0}</td>
                    <td>{row.goodOrGreatDealCount ?? 0}</td>
                    <td>{row.overpricedCount ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="vehicle-analytics-page__cards">
            <h2>By vehicle class</h2>
            {classes.length ? (
              <div className="vehicle-analytics-page__card-grid">
                {classes.map((c) => (
                  <VehicleClassAnalyticsCard key={c.vehicleClass} classSummary={c} />
                ))}
              </div>
            ) : (
              <p className="muted">No classes match the current filters.</p>
            )}
          </section>
        </>
      ) : null}
    </div>
  );
};

export default VehicleMarketAnalyticsPage;
